import React, { forwardRef, memo, ReactNode, useRef, useState } from "react";
import classNames from "classnames";
import BaseModal, { Classes, Props } from "react-modal";
import { Overlay, OverlayProps } from "../Overlay";
import { SideoutMenuChildrenProps } from "./components/SideoutMenuChildren";
import { SideoutMenuChildren } from "./components";
import { findParentWithNonStaticPosition, useCheckWindowRenderTypeRequired } from "./utils";

import styles from "./styles.module.scss";

export type SideoutMenuProps = Props & {
  renderType?: "block" | "window"
  scrollable?: boolean
  header?: ReactNode | ReactNode[]
  sidebarContainerClassName?: string,
  headerClassName?: string
  footer?: ReactNode | ReactNode[]
  footerClassName?: string,
  overlayProps?: OverlayProps
  sideoutMenuChildrenProps?: SideoutMenuChildrenProps
};

export const SideoutMenu = memo(
  forwardRef<BaseModal, SideoutMenuProps>(
    (
      {
        renderType: requestedRenderType = "window",
        scrollable = true,
        header,
        headerClassName,
        footer,
        footerClassName,
        children,
        className,
        overlayProps,
        sidebarContainerClassName,
        sideoutMenuChildrenProps,
        ...props
      },
      ref,
    ) => {
      const [ parent, setParent ] = useState<HTMLElement | null>();
      const targetRef = useRef<HTMLElement | null>(null);
      const renderType = useCheckWindowRenderTypeRequired(targetRef) ? "window" : requestedRenderType;

      return (
        <>
          <div className={classNames(styles.sidebarContainer, sidebarContainerClassName)} ref={(node) => {
            setParent(node);
            (targetRef.current as any) = findParentWithNonStaticPosition(node?.parentElement);
          }} />
          <BaseModal
            parentSelector={(renderType === "block" && parent) ? () => parent : undefined}
            className={(!className || typeof className === "string")
              ? classNames(styles.root, className, {})
              : {
                ...className,
                base: classNames(styles.root, className?.base),
              } as Classes
            }
            overlayElement={(props, contentEl) => (
              <Overlay backgroundType="gradient" {...props} {...overlayProps}>
                {contentEl}
              </Overlay>
            )}
            {...props}
            style={{
              ...props.style,
              overlay: {
                position: renderType === "block" ? "absolute" : "fixed",
                ...props.style?.overlay,
              },
              content: {
                gridTemplateRows: [
                  header && "auto",
                  "1fr",
                  footer && "auto",
                ].filter(Boolean)
                  .join(" "),
                ...props.style?.content,
              },
            }}
            ref={ref}
          >
            {header && (
              <div className={classNames(styles.header, headerClassName)}>
                {header}
              </div>
            )}
            <SideoutMenuChildren
              scrollable={scrollable}
              {...sideoutMenuChildrenProps}
            >
              {children}
            </SideoutMenuChildren>
            {footer && (
              <div className={classNames(styles.footer, footerClassName)}>
                {footer}
              </div>
            )}
          </BaseModal>
        </>
      );
    },
  ),
);
